import React from 'react';
import { Box } from '@mui/material';
import { isInternalPlanUser } from '../utils/featureAccess';

/**
 * DevOnly Component
 *
 * Wraps content that should only be visible to users on the internal/dev plan.
 * Checks subscription tier instead of environment to ensure proper access control.
 *
 * Usage:
 * <DevOnly>
 *   <IconButton onClick={handleEdit}>
 *     <EditIcon />
 *   </IconButton>
 * </DevOnly>
 *
 * Or with fallback:
 * <DevOnly fallback={<span>Read Only</span>}>
 *   <IconButton onClick={handleEdit}>
 *     <EditIcon />
 *   </IconButton>
 * </DevOnly>
 */
const DevOnly = ({ children, fallback = null, show = null }) => {
  // Check if user is on internal/dev plan
  const isInternalUser = isInternalPlanUser();

  // Allow manual override with show prop
  const shouldShow = show !== null ? show : isInternalUser;

  if (!shouldShow) {
    return fallback;
  }

  return <>{children}</>;
};

/**
 * DevOnlyWrapper Component
 *
 * Similar to DevOnly but with visual indication for internal plan users.
 * Adds a subtle border or background to indicate dev-only content.
 *
 * Usage:
 * <DevOnlyWrapper>
 *   <Button onClick={handleCreate}>Create New</Button>
 * </DevOnlyWrapper>
 */
export const DevOnlyWrapper = ({ children, showIndicator = false, ...props }) => {
  const isInternalUser = isInternalPlanUser();

  if (!isInternalUser) {
    return null;
  }

  if (showIndicator) {
    return (
      <Box
        {...props}
        sx={{
          position: 'relative',
          '&::after': {
            content: '"INTERNAL"',
            position: 'absolute',
            top: -8,
            right: -8,
            fontSize: '10px',
            backgroundColor: 'warning.main',
            color: 'white',
            padding: '2px 4px',
            borderRadius: '4px',
            fontWeight: 'bold',
            zIndex: 1000,
          },
          ...props.sx
        }}
      >
        {children}
      </Box>
    );
  }

  return <>{children}</>;
};

export default DevOnly;